import { escopoDoUsuario } from "./escopo";

/* O escopo de INTERFACE: quais ids o painel manda nas chamadas.

   O defeito que estes casos travam (relatado pelo Kilson em 04/08/2026): o
   Administrador Educandus escolhia a rede no modal "Escolha sua rede" do AVA e o
   painel respondia "Não foi possível identificar a sua rede". A causa é que o
   escopo saía só do VÍNCULO (`user.user_roles`), e o vínculo de tipo G é vazio de
   propósito — ele é irrestrito. Medido na homologação: 33 de 33 vínculos de
   Administrador Educandus com group_id NULL, incluindo o do usuário de teste
   (caio.mizan, id 414965).

   Quem sabe a rede escolhida é `general.active_roles`, que o modal atualiza
   preservando o role_id. */

const vinculo = (roleId, ids = {}) => ({
  role_id: roleId,
  group_id: null,
  region_id: null,
  school_id: null,
  ...ids
});

const ativo = (roleId, ids = {}) => ({
  role_id: roleId,
  group_id: null,
  region_id: null,
  school_id: null,
  ...ids
});

describe("Administrador Educandus (papel 1, vínculo vazio)", () => {
  it("usa a rede escolhida no modal do AVA", () => {
    const escopo = escopoDoUsuario(
      [vinculo(1)], // vínculo sem grupo, como é na base
      ativo(1, { group_id: 80 }) // o que o modal gravou
    );

    expect(escopo.grupoId).toBe(80);
  });

  it("sem rede escolhida ('Todos'), fica sem grupo — e isso não é erro", () => {
    // null aqui significa "todas as redes": o querystring omite o parâmetro e o
    // backend, irrestrito, devolve a base inteira. O GestorApp trata como caso
    // válido quando o escopo é "todos", em vez de "não foi possível identificar".
    const escopo = escopoDoUsuario([vinculo(1)], ativo(1));

    expect(escopo.grupoId).toBeNull();
    expect(escopo.escopo).toBe("todos");
  });

  it("acompanha a troca de rede sem depender do vínculo", () => {
    const roles = [vinculo(1)];

    expect(escopoDoUsuario(roles, ativo(1, { group_id: 80 })).grupoId).toBe(80);
    expect(escopoDoUsuario(roles, ativo(1, { group_id: 97 })).grupoId).toBe(97);
  });
});

describe("a escolha no AVA vence o vínculo", () => {
  it("gestor de rede que troca de grupo no modal vê o grupo escolhido", () => {
    const escopo = escopoDoUsuario(
      [vinculo(2, { group_id: 80 })],
      ativo(2, { group_id: 97 })
    );

    expect(escopo.grupoId).toBe(97);
  });

  it("cai para o vínculo quando o modal não escolheu nada", () => {
    const escopo = escopoDoUsuario([vinculo(2, { group_id: 80 })], ativo(2));

    expect(escopo.grupoId).toBe(80);
  });

  it("vale para região e escola também", () => {
    const escopo = escopoDoUsuario(
      [vinculo(4, { group_id: 80, region_id: 171, school_id: 348 })],
      ativo(4, { group_id: 80, region_id: 172, school_id: 349 })
    );

    expect(escopo.regiaoId).toBe(172);
    expect(escopo.escolaId).toBe(349);
  });
});

describe("só o papel ativo entra no escopo", () => {
  it("ignora os vínculos dos outros papéis do usuário", () => {
    // Professor na escola 5 e administrador escolar na 9. Com o papel de
    // professor ativo, a escola 9 não pode aparecer: recortar por ela mandaria o
    // painel pedir dado de uma escola que não é a do papel atual.
    const escopo = escopoDoUsuario(
      [
        vinculo(6, { group_id: 1, school_id: 5 }),
        vinculo(4, { group_id: 1, school_id: 9 })
      ],
      ativo(6, { group_id: 1 })
    );

    expect(escopo.escolaId).toBe(5);
    expect(escopo.escopo).toBe("turma");
  });

  it("dois vínculos do mesmo papel com escolas diferentes dão null", () => {
    // null é "mais de um", e a tela precisa deixar o usuário escolher em vez de
    // assumir a primeira.
    const escopo = escopoDoUsuario(
      [
        vinculo(6, { group_id: 1, school_id: 5 }),
        vinculo(6, { group_id: 1, school_id: 9 })
      ],
      ativo(6, { group_id: 1 })
    );

    expect(escopo.escolaId).toBeNull();
    expect(escopo.escolaIds.sort()).toEqual([5, 9]);
  });
});

describe("entradas ausentes não derrubam o escopo", () => {
  it("aceita active_roles nulo e lista vazia", () => {
    expect(escopoDoUsuario([], null)).toEqual(
      expect.objectContaining({
        grupoId: null,
        regiaoId: null,
        escolaId: null,
        escopo: null
      })
    );
    expect(escopoDoUsuario(undefined, undefined).grupoId).toBeNull();
  });
});
